import { PermissionGate } from "@components/PermissionGate";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  Group,
  Modal,
  ScrollAreaAutosize,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";

import { formatDateToString } from "@utils/dateValidation";
import { truncateWithEllipsis } from "@utils/truncateText";

import { useDeleteMedicalRecord } from "@hooks/mutations/patient/useDeleteMedicalRecord";

import { PatientMedicalHistoryItem } from "@customTypes/patient";

import { StyledFullMedicalHistoryModal } from "./FullMedicalHistoryModal.styled";


interface FullMedicalHistoryModalProps {
  medicalHistory?: PatientMedicalHistoryItem[];
  opened: boolean;
  close: () => void;
}

export const FullMedicalHistoryModal = ({
  medicalHistory = [],
  opened,
  close,
}: FullMedicalHistoryModalProps) => {
  const { t } = useTranslation();
  // const test = [...medicalHistory, ...medicalHistory];
  const { patientId } = useParams();

  const [currentId, setCurrentId] = useState("");
  const { mutate: deleteMedicalRecord, isLoading: isDeletingMedicalRecord } =
    useDeleteMedicalRecord();

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("full-medical-history")}</Text>}
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledFullMedicalHistoryModal>
        <ul className="timeline">
          {medicalHistory.map(
            ({ date, condition, origin, status, id }, index) => (
              <li className="timeline-item" key={index}>
                <span className="date">
                  {formatDateToString(date).slice(-7)}
                </span>
                <span aria-hidden className="decor"></span>
                <div className="wrapper">
                  <Group gap={"xs"}>
                    <p
                      className="name"
                      title={condition.length > 30 ? condition : ""}>
                      {truncateWithEllipsis(condition, 30)}
                    </p>
                    <Tooltip
                      withArrow
                      events={{ focus: true, hover: true, touch: true }}
                      label={t("delete")}>
                      <PermissionGate permission="consultations.delete"><ActionIcon
                        size={"sm"}
                        variant="outline"
                        color="red"
                        loading={isDeletingMedicalRecord && id === currentId}
                        className="delete-btn"
                        onClick={() => {
                          setCurrentId(id);
                          deleteMedicalRecord({
                            recordId: id,
                            patientId: patientId as string,
                          });
                        }}>
                        <IconTrash size={14} />
                      </ActionIcon></PermissionGate>
                    </Tooltip>
                  </Group>
                  <ul className="tags">
                    <li className="tags-item">
                      <span className={origin === "family" ? "orange" : "blue"}>
                        {t(`${origin}`)}
                      </span>
                    </li>
                    <li className="tags-item">
                      <span className={status === "active" ? "red" : "green"}>
                        {t(`${status}`)}
                      </span>
                    </li>
                  </ul>
                </div>
              </li>
            )
          )}
        </ul>
      </StyledFullMedicalHistoryModal>
    </Modal>
  );
};
